import { cva, type VariantProps } from "class-variance-authority";
import type { LucideIcon } from "lucide-react";
import type * as React from "react";

import { cn } from "../lib/cn";

export const badgeVariants = cva(
  "inline-flex h-[22px] items-center gap-1 whitespace-nowrap rounded-sm border border-transparent px-2 text-xs font-medium [&_svg]:size-3 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground",
        secondary: "bg-secondary text-secondary-foreground",
        outline: "border-border bg-transparent text-foreground",
        soft: "bg-primary-soft text-primary",
        success: "bg-success-soft text-success",
        warning: "bg-warning-soft text-warning",
        destructive: "bg-destructive-soft text-destructive",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

export interface BadgeProps extends React.ComponentProps<"span">, VariantProps<typeof badgeVariants> {
  icon?: LucideIcon;
}

/** A short status or count: one or two words. Status badges always carry the word. */
export function Badge({ className, variant, icon: Glyph, children, ...props }: BadgeProps) {
  return (
    <span data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props}>
      {Glyph ? <Glyph aria-hidden strokeWidth={1.75} /> : null}
      {children}
    </span>
  );
}

export type PartyRole = "maker" | "buyer" | "supplier" | "designer" | "equipment";

const ROLE_LABEL: Record<PartyRole, string> = {
  maker: "Үйлдвэрлэгч",
  buyer: "Худалдан авагч",
  supplier: "Нийлүүлэгч",
  designer: "Дизайнер",
  equipment: "Тоног төхөөрөмж",
};

const ROLE_COLOR: Record<PartyRole, string> = {
  maker: "bg-chart-1",
  buyer: "bg-chart-2",
  supplier: "bg-chart-3",
  designer: "bg-chart-4",
  equipment: "bg-chart-5",
};

/** What a company is in the network; the square uses the role's chart colour. */
export function RoleTag({
  role,
  children,
  className,
}: {
  role: PartyRole;
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      data-slot="role-tag"
      data-role={role}
      className={cn(
        "inline-flex h-[22px] items-center gap-1.5 whitespace-nowrap rounded-sm border border-border bg-card pr-2 pl-1.5 text-xs font-medium text-foreground",
        className,
      )}
    >
      <span aria-hidden className={cn("size-2 rounded-[2px]", ROLE_COLOR[role])} />
      {children ?? ROLE_LABEL[role]}
    </span>
  );
}
